import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

export interface GridListProps {
	title?: string
	subtitle?: string
	items: {
		title: string
		description: string
		icon?: string
		href?: string
	}[]
	columns?: number
	variant?: 'cards' | 'simple' | 'stats'
	background?: 'default' | 'muted' | 'primary-light'
}

export function GridList({
	title,
	subtitle,
	items,
	columns = 3,
	variant = 'cards',
	background = 'default',
}: GridListProps) {
	const bgClasses = {
		default: 'bg-background',
		muted: 'bg-muted/30',
		'primary-light': 'bg-primary/5',
	}

	const gridCols = {
		1: 'grid-cols-1',
		2: 'grid-cols-1 sm:grid-cols-2',
		3: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
		4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4',
	}

	return (
		<section className={cn('py-16 md:py-20', bgClasses[background])}>
			<div className="container mx-auto px-4">
				{(title || subtitle) && (
					<div className="mx-auto mb-10 flex max-w-3xl flex-col gap-4 text-center">
						{title && <h2 className="text-title-2 text-foreground">{title}</h2>}
						{subtitle && (
							<p className="text-muted-foreground text-base md:text-lg">
								{subtitle}
							</p>
						)}
					</div>
				)}

				<div
					className={cn(
						'grid gap-6',
						gridCols[columns as keyof typeof gridCols] || gridCols[3],
					)}
				>
					{items.map((item, index) => {
						const cardClassName = cn(
							'flex flex-col gap-4',
							variant === 'cards' &&
								'border-border bg-card rounded-2xl border p-6 shadow-sm',
							variant === 'stats' &&
								'border-border bg-background rounded-lg border p-6 shadow-sm',
							variant === 'simple' &&
								'border-border bg-muted/20 rounded-lg border p-4 shadow-sm',
						)

						const content = (
							<>
								{item.icon && (
									<div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-full">
										<Icon name={item.icon as any} className="size-5" />
									</div>
								)}
								<div className={cn('space-y-2', variant === 'stats' && 'mt-0')}>
									<h3
										className={cn(
											'text-foreground font-semibold',
											variant === 'stats'
												? 'text-primary text-4xl md:text-5xl'
												: 'text-lg',
										)}
									>
										{item.title}
									</h3>
									<p
										className={cn(
											'text-muted-foreground',
											variant === 'stats'
												? 'mt-2 text-sm font-medium'
												: 'text-sm',
											variant === 'simple' && 'mt-1 text-xs',
										)}
									>
										{item.description}
									</p>
								</div>
							</>
						)

						return (
							<div key={index} className={cardClassName}>
								{content}
								{item.href ? (
									<Link
										to={item.href}
										className="text-primary mt-auto inline-flex items-center pt-2 text-sm font-medium"
									>
										Leer más
									</Link>
								) : null}
							</div>
						)
					})}
				</div>
			</div>
		</section>
	)
}
